/**
 * Path 2: Cost Engine — Multi-provider model routing.
 *
 * 4 scenes showing how Cortex routes tasks to the cheapest capable model:
 * the router, batch intelligence, feedback ROI, provider resilience.
 */

import type { AdventurePath } from './paths';

export const COST_ENGINE_PATH: AdventurePath = {
  id: 'cost',
  title: 'Cost Engine',
  subtitle: '6 providers, 12 models, 1 routing brain',
  scenes: [
    // ── Scene 1: The Router ──────────────────────────
    {
      id: 'cost-1',
      title: 'The Router',
      lines: [
        { text: '$ cortex conductor status --today', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Conductor Routing Summary (24h) ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Tasks routed: 12', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  USE CASE           PROVIDER    MODEL          COST', style: 'dim' },
        { text: '  --------------------------------------------------------', style: 'dim' },
        { text: '  classification     Groq        Llama 8B       $0.003', style: 'data' },
        { text: '  quick_qa           Groq        GPT-OSS 20B    $0.008', style: 'data' },
        { text: '  documentation      DeepSeek    V3.2           $0.014', style: 'data' },
        { text: '  pattern_learning   DeepSeek    V3.2           $0.014', style: 'data' },
        { text: '  research           xAI         Grok 4.1 Fast  $0.028', style: 'data' },
        { text: '  long_context       xAI         Grok 4.1 Fast  $0.031', style: 'data' },
        { text: '  code_review        MiniMax     M2.1 Coder     $0.048', style: 'data' },
        { text: '  test_generation    MiniMax     M2.1 Coder     $0.052', style: 'data' },
        { text: '  interactive_coding Anthropic   Sonnet 4.5     $0.210', style: 'data' },
        { text: '  architecture       Anthropic   Opus 4.6       $0.480', style: 'warning' },
        { text: '  security_audit     Anthropic   Opus 4.6       $0.520', style: 'warning' },
        { text: '  --------------------------------------------------------', style: 'dim' },
        { text: '  TOTAL                                         $1.408', style: 'data' },
        { text: '  Anthropic-only baseline                       $8.340', style: 'dim' },
        { text: '  Savings                                       83.1%', style: 'success' },
        { text: '', style: 'blank' },
        { text: 'Same day. Same tasks. Same quality.', style: 'narration' },
        { text: '83% cheaper because classification does not', style: 'narration' },
        { text: 'need a $15/MTok model.', style: 'narration' },
      ],
      hubEvent: {
        task: 'Security audit (auth module)',
        complexity: 'complex',
        selectedProvider: 'Anthropic',
        selectedModel: 'Opus 4.6',
        cost: 0.52,
        baselineCost: 0.52,
        reason: 'Security requires frontier reasoning. No savings here -- quality > cost.',
        fallback: 'OpenAI GPT-5',
      },
      choices: [
        { label: 'See batch intelligence (overnight queue)', next: 'cost-2', tag: 'BATCH' },
        { label: 'Skip to provider resilience', next: 'cost-4', tag: 'FAILOVER' },
      ],
    },

    // ── Scene 2: Batch Intelligence ──────────────────
    {
      id: 'cost-2',
      title: 'Batch Intelligence',
      lines: [
        { text: '$ cortex batch --status', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Overnight Batch Queue ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Queued at 23:00. Completed by 06:00.', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  TASK                  PROVIDER     BATCH PRICE', style: 'dim' },
        { text: '  -----------------------------------------------', style: 'dim' },
        { text: '  14x code_review       MiniMax      $0.31 (50% off)', style: 'success' },
        { text: '  8x test_generation    MiniMax      $0.18 (50% off)', style: 'success' },
        { text: '  3x security_audit     Anthropic    $0.78 (50% off)', style: 'success' },
        { text: '  21x documentation     DeepSeek     $0.14 (no batch)', style: 'data' },
        { text: '  -----------------------------------------------', style: 'dim' },
        { text: '  Batch total:                       $1.41', style: 'data' },
        { text: '  Real-time equivalent:              $2.83', style: 'dim' },
        { text: '  Batch savings:                     50.2%', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Providers with batch API:', style: 'header' },
        { text: '  MiniMax  -- 50% off all models', style: 'data' },
        { text: '  Anthropic -- 50% off all Claude models', style: 'data' },
        { text: '  OpenAI   -- 50% off all GPT models', style: 'data' },
        { text: '  (Groq, xAI, DeepSeek -- no batch support)', style: 'dim' },
        { text: '', style: 'blank' },
        { text: 'Non-urgent work runs overnight at half price.', style: 'narration' },
        { text: 'Code reviews, doc generation, security scans --', style: 'narration' },
        { text: 'ready when you arrive Monday morning.', style: 'narration' },
      ],
      choices: [
        { label: 'See the 6-month ROI calculation', next: 'cost-3', tag: 'ROI' },
        { label: 'See provider failover in action', next: 'cost-4', tag: 'RESILIENCE' },
      ],
    },

    // ── Scene 3: The Feedback ROI ────────────────────
    {
      id: 'cost-3',
      title: 'The Feedback ROI',
      lines: [
        { text: '$ cortex metrics --roi --period 6m', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== 6-Month Cost Intelligence ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Total tasks routed:     4,218', style: 'data' },
        { text: '  Total tokens processed: 847M', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  MONTH     ROUTED   BASELINE   SAVINGS', style: 'dim' },
        { text: '  -------------------------------------------', style: 'dim' },
        { text: '  Oct       $89      $412       78.4%', style: 'data' },
        { text: '  Nov       $76      $398       80.9%', style: 'data' },
        { text: '  Dec       $71      $445       84.0%', style: 'success' },
        { text: '  Jan       $68      $467       85.4%', style: 'success' },
        { text: '  Feb       $63      $489       87.1%', style: 'success' },
        { text: '  Mar       $52      $501       89.6%', style: 'success' },
        { text: '  -------------------------------------------', style: 'dim' },
        { text: '  Total:    $419     $2,712     84.6%', style: 'success' },
        { text: '  Saved:    $2,293 in 6 months', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Trend: savings INCREASING each month.', style: 'accent' },
        { text: '  Why? Feedback loop learns which models', style: 'narration' },
        { text: '  actually deliver quality for each task type.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'The routing gets smarter over time.', style: 'narration' },
        { text: 'Month 1: 78%. Month 6: 90%. Same quality.', style: 'accent' },
        { text: 'Because outcomes are measured, not assumed.', style: 'narration' },
      ],
      choices: [
        { label: 'See provider failover in action', next: 'cost-4', tag: 'RESILIENCE' },
        { label: 'Explore another path', next: 'hub', tag: 'PATHS' },
      ],
    },

    // ── Scene 4: Provider Resilience ─────────────────
    {
      id: 'cost-4',
      title: 'Provider Resilience',
      lines: [
        { text: '$ cortex conductor --health', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Provider Health Monitor ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Groq       HEALTHY   p50: 180ms   uptime: 99.8%', style: 'success' },
        { text: '  xAI        HEALTHY   p50: 420ms   uptime: 99.2%', style: 'success' },
        { text: '  MiniMax    HEALTHY   p50: 890ms   uptime: 98.9%', style: 'success' },
        { text: '  Anthropic  DEGRADED  p50: 3,400ms uptime: 97.1%', style: 'warning' },
        { text: '  OpenAI     HEALTHY   p50: 340ms   uptime: 99.5%', style: 'success' },
        { text: '  DeepSeek   HEALTHY   p50: 560ms   uptime: 99.1%', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  ALERT: Anthropic latency spike detected', style: 'error' },
        { text: '  Current p50: 3,400ms (baseline: 1,200ms)', style: 'warning' },
        { text: '', style: 'blank' },
        { text: '  Auto-rerouting activated:', style: 'header' },
        { text: '  architecture    Anthropic Opus -> OpenAI GPT-5', style: 'data' },
        { text: '  security_audit  Anthropic Opus -> OpenAI GPT-5', style: 'data' },
        { text: '  coding          Anthropic Sonnet -> xAI Grok Code', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Rerouted tasks: 3 | User impact: 0', style: 'success' },
        { text: '  Recovery ETA: ~15 minutes', style: 'dim' },
        { text: '', style: 'blank' },
        { text: 'Single-provider = single point of failure.', style: 'narration' },
        { text: 'Six providers = automatic resilience.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'Every route has a fallback. Every fallback', style: 'accent' },
        { text: 'is pre-validated for quality. Zero downtime.', style: 'accent' },
      ],
      hubEvent: {
        task: 'Architecture review (auto-rerouted)',
        complexity: 'complex',
        selectedProvider: 'OpenAI',
        selectedModel: 'GPT-5',
        cost: 0.38,
        baselineCost: 0.48,
        reason: 'Anthropic degraded. Auto-failover to GPT-5. Quality validated.',
        fallback: 'Anthropic Opus 4.6 (recovering)',
      },
      choices: [
        { label: 'Explore another path', next: 'hub', tag: 'PATHS' },
        { label: 'See portfolio intelligence', next: 'portfolio-1', tag: 'PORTFOLIO' },
      ],
    },
  ],
};
